import { useEffect, useState, useCallback } from "react";
import { useHistory, Link } from "react-router-dom";
import axios from "axios";
import { Trophy, ClockHistory, StarFill, ChevronRight } from "react-bootstrap-icons";
import notify from "../../common/ui/notify";
import PageHero from "../../common/ui/PageHero";
import { LoadingBlock } from "../../common/ui/StateBlock";
import { formatPrice, carTitle, formatDateTime } from "../../common/format";
import { emitAck } from "../../common/auctionSocket";
import useWatchlist from "../../common/useWatchlist";
import { t } from "./auctionI18n";
import "./Auction.css";
import "../../common/ui/ui.css";

const AuctionMy = () => {
    const history = useHistory();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const watch = useWatchlist();

    const load = useCallback(async () => {
        try {
            const res = await axios.get("/api/auction/my/activity");
            setData(res.data);
        } catch (err) {
            if (err && err.response && err.response.status === 401) { history.replace("/auction/login"); return; }
            notify.apiError(err);
        } finally { setLoading(false); }
    }, [history]);

    useEffect(() => { load(); }, [load]);

    const respondCounter = async (lotId, accept) => {
        if (busy) return;
        setBusy(true);
        try {
            const ack = await emitAck("counter:respond", { lot_id: lotId, accept });
            if (ack && ack.ok) {
                notify.success(accept
                    ? { en: "Offer accepted — an invoice has been created.", geo: "შეთავაზება მიღებულია — ინვოისი შეიქმნა.", ru: "Предложение принято — счёт создан.", am: "Առաջարկն ընդունված է — հաշիվը ստեղծվել է։" }
                    : { en: "Offer declined.", geo: "შეთავაზებაზე უარი თქვით.", ru: "Предложение отклонено.", am: "Առաջարկը մերժվեց։" });
                await load();
            } else {
                notify.error({ en: `Failed: ${ack && ack.reason}`, geo: `ვერ შესრულდა: ${ack && ack.reason}`, ru: `Не удалось: ${ack && ack.reason}`, am: `Ձախողվեց: ${ack && ack.reason}` });
            }
        } finally { setBusy(false); }
    };

    if (loading) return <div className="container auction-narrow"><LoadingBlock label={t("იტვირთება...", "Loading...", "Загрузка...", "Բեռնվում է...")} /></div>;
    if (!data) return null;

    const { counters = [], invoices = [], bid_history = [] } = data;
    const activeBids = bid_history.filter((b) => b.result === "leading" || b.result === "outbid").length;
    const watchCount = (watch.rows || []).length;

    const tiles = [
        { to: "/auction/won", icon: <Trophy />, label: t("მოგებული ავტომობილები", "Won cars", "Выигранные авто", "Շահած մեքենաներ"), count: invoices.length, hint: t("ინვოისები და გადახდა", "Invoices and payment", "Счета и оплата", "Հաշիվներ և վճարում"), tone: "won" },
        { to: "/auction/bids", icon: <ClockHistory />, label: t("ბიდების ისტორია", "Bid history", "История ставок", "Հայտերի պատմություն"), count: bid_history.length, hint: activeBids ? t(`${activeBids} აქტიური`, `${activeBids} active`, `${activeBids} активных`, `${activeBids} ակտիվ`) : t("ყველა ბიდი", "All bids", "Все ставки", "Բոլոր հայտերը"), tone: "bids" },
        { to: "/auction/watchlist", icon: <StarFill />, label: t("ჩემი რჩეულები", "My favorites", "Моё избранное", "Իմ ընտրանին"), count: watchCount, hint: t("შენახული ავტომობილები", "Saved cars", "Сохранённые авто", "Պահպանված մեքենաներ"), tone: "watch" },
    ];

    return (
        <>
            <PageHero eyebrow="Carexpress" title={t("ჩემი აუქციონები", "My auctions", "Мои аукционы", "Իմ աճուրդները")} subtitle={t("თქვენი აუქციონის აქტივობის მართვის ცენტრი.", "The hub for managing your auction activity.", "Центр управления вашей аукционной активностью.", "Ձեր աճուրդային ակտիվության կառավարման կենտրոն։")} />
            <div className="container auction-narrow auction-wide auction-my">
                <Link to="/auction/account" className="auction-back">← {t("ანგარიში", "Account", "Аккаунт", "Հաշիվ")}</Link>

                {/* Time-sensitive counter-offers surface on the dashboard */}
                {counters.length > 0 && (
                    <section className="cx-card auction-my-section auction-my-counters">
                        <h3>⏳ {t("საპასუხო შეთავაზებები", "Counter-offers", "Встречные предложения", "Հակաառաջարկներ")}</h3>
                        {counters.map((c) => (
                            <div key={c.lot_id} className="auction-my-counter">
                                <div>
                                    <strong>{carTitle(c)}</strong>
                                    <p className="auction-muted">
                                        {t("თქვენი ბიდი", "Your bid", "Ваша ставка", "Ձեր հայտը")} {formatPrice(c.winning_bid, "GEL")} · {t("შეთავაზება", "Offer", "Предложение", "Առաջարկ")} <strong className="auction-counter-price">{formatPrice(c.counter_price, "GEL")}</strong>
                                    </p>
                                    <p className="auction-muted">{t("ვადა", "Expires", "Срок", "Ժամկետ")}: {formatDateTime(c.counter_expires_at)}</p>
                                </div>
                                <div className="auction-my-counter-actions">
                                    <button className="cx-btn cx-btn-primary" disabled={busy} onClick={() => respondCounter(c.lot_id, true)}>{t("თანხმობა", "Accept", "Принять", "Ընդունել")}</button>
                                    <button className="cx-btn cx-btn-ghost" disabled={busy} onClick={() => respondCounter(c.lot_id, false)}>{t("უარი", "Decline", "Отклонить", "Մերժել")}</button>
                                </div>
                            </div>
                        ))}
                    </section>
                )}

                {/* Dashboard tiles → separate pages */}
                <div className="auction-dash-grid">
                    {tiles.map((tile) => (
                        <Link key={tile.to} to={tile.to} className={`cx-card auction-dash-tile tone-${tile.tone}`}>
                            <span className="auction-dash-ico">{tile.icon}</span>
                            <span className="auction-dash-count">{tile.count}</span>
                            <span className="auction-dash-label">{tile.label}</span>
                            <span className="auction-dash-hint">{tile.hint} <ChevronRight /></span>
                        </Link>
                    ))}
                </div>
            </div>
        </>
    );
};

export default AuctionMy;
